import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getCbtToken } from "shared/cbt/cbt";
import { ROUTES } from "shared/constants";
import {
  CHILD_WITHOUT_ACCOMPANIENT_ERROR_CODE,
  DEPARTURE_BEFORE_PREVIOUS_FLIGHT_MULTICITY,
  INCORRECT_ERROR_CODE,
  INVALID_DATE_ERROR_CODE,
} from "shared/constants/results/errors";
import { cbtPassengersFormParamsFromQuery } from "shared/data/actions/cbt";
import { stopSearchLoading } from "shared/data/actions/loader";
import { flightSearch, toggleServiceInfo } from "shared/data/actions/search";
import { AppState } from "shared/data/reducers";
import { isFalsy } from "shared/lib/isFalsy";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";
import Logger from "shared/services/Logger";
import type { Results as ResultsProps } from "shared/typescript/basicTypes/Results";
import type { SearchQuery } from "shared/typescript/storage";

import Img from "@components/FileServer/Img";
import Filters from "@components/Filters/Filters";
import SearchSorting from "@components/Filters/SearchSorting";
import { prepareFlightSortingDetails } from "@components/Filters/searchSortingDetails";
import ListFlights from "@components/Flights/ListFlights";
import SearchSteps from "@components/UI/Header/SearchSteps";
import ShareModal from "@components/UI/ShareModal";
import { LegacyModalAlert } from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer, searchDataLayer } from "@lib/dataLayers";
import { routeChangeFallback } from "@lib/webFunctions";
import { setBookingFormValue } from "@shared/data/actions/bookingForm";

const { title } = defineMessages({
  title: formattedMessageParams("Screen.Results.title"),
});

const FILTER_OPENING_TIME = 1100;
const CLICK_DELAY = 500;
const FILTER_OPENING_DELAY = 150;
const ANIM_FOR_STEPPING_BACK = 0;

const Results = (props: ResultsProps) => {
  const router = useRouter();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [errorCode, setErrorCode] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [filterButtonClickable, setFilterButtonClickable] = useState(true);
  const [clickTimestamp, setClickTimestamp] = useState<number | null>(null);
  const [debugIdx, setDebugIdx] = useState<number>(0);
  const [queryModel, setQueryModel] = useState<SearchQuery>({});
  const [isFloating, setIsFloating] = useState(true);
  const [isSomeModalOpenned, setIsSomeModalOpenned] = useState(false);

  const {
    user,
    isSearching,
    gtmId,
    flightOffers,
    searchQuery,
    frontendSettings,
    customerUsername,
    cbtApiUrl,
    allowedTravelerTypes,
    travelers,
    blockSearchDaysBefore,
  } = useSelector((state: AppState) => ({
    flightOffers: state.search.flights,
    searchQuery: state.storage.searchQuery,
    isSearching: state.loading.searchLoader,
    frontendSettings: state.storage.frontendSettings,
    user: state.user,
    customerUsername: state.user.email,
    cbtApiUrl:
      state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl,
    allowedTravelerTypes: state.requestorConfig.allowedTravelerTypes,
    travelers: state.cbt.users,
    blockSearchDaysBefore:
      state.storage.frontendSettings?.dealerFlightSearchSettings
        ?.blockSearchDaysBefore,
    gtmId:
      state.storage.frontendSettings?.dealerGoogleSettings
        ?.tagManagerContainerId,
  }));

  const searchSortingDetails = prepareFlightSortingDetails(
    flightOffers,
    router.query
  );

  const sortingRef = useRef(null);
  const START_PIN_FILTER_FROM = 120;
  const PINNED_FROM_TOP = 225;

  useEffect(() => {
    if (gtmId) {
      searchDataLayer(searchQuery);
    }
    pageDataLayer("results", user);
  }, [gtmId, searchQuery, user]);

  useEffect(() => {
    const sortingRefElement = sortingRef.current;
    const handleScroll = () => {
      const scrollPosition =
        window.scrollY + window.innerHeight - START_PIN_FILTER_FROM;
      const sortingRefPosition = sortingRefElement
        ? sortingRefElement.getBoundingClientRect().top + window.scrollY
        : 0;

      setIsFloating(scrollPosition >= sortingRefPosition);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortingRef.current]);

  const goBack = () => {
    routeChangeFallback(() => {
      router.push(ROUTES.HOME);
    }, router);
    setErrorMsg(null);
  };

  const showError = (eMsg, eCode, isIntl) => {
    setErrorCode(eCode);
    setErrorMsg(isIntl ? intl.formatMessage({ id: eMsg }) : eMsg);
  };

  const hideCustomError = () => {
    setErrorMsg(null);
    goBack();
  };

  const flightSearchHandler = async () => {
    const cbtToken = await getCbtToken(
      user,
      frontendSettings,
      customerUsername
    );
    const searchQueryParams = { ...props.router.query };
    delete searchQueryParams.minimizeSearchForm;

    const cbtPassengersForm =
      cbtToken && cbtPassengersFormParamsFromQuery(searchQueryParams);

    const flights = await dispatch(
      flightSearch(searchQueryParams, {
        cbtToken,
        cbtPassengersForm,
        customerUsername,
        cbtApiUrl,
        allowedTravelerTypes,
        blockSearchDaysBefore,
        travelers,
      })
    );

    if (flights?.error && flights?.type === "FLIGHT_SEARCH_FAIL") {
      dispatch(stopSearchLoading());
      return showError(flights?.error, flights?.errorCode, flights?.isIntl);
    }
  };

  const onKeyDownHandler = (event) => {
    const { key } = event;
    if ("gd"[debugIdx] === key) {
      setDebugIdx(debugIdx + 1);
    } else if (debugIdx !== 0 && !"gd".includes(key)) {
      setDebugIdx(0);
    }
  };

  const onKeyUpHandler = () => {
    if (debugIdx !== 0) {
      setDebugIdx(0);
    }
  };

  useEffect(() => {
    addEventListener("keyup", onKeyUpHandler);
    return () => {
      addEventListener("keyup", onKeyUpHandler);
    };
  });

  useEffect(() => {
    addEventListener("keydown", onKeyDownHandler);
    if (debugIdx === "gd".length) {
      dispatch(toggleServiceInfo());
    }
    return () => {
      removeEventListener("keydown", onKeyDownHandler);
    };
  });

  useEffect(() => {
    if (router.query.date && router.query.date === queryModel.date) {
      return;
    }

    if (
      router.query.guestsGradeIds &&
      travelers?.length === 0 &&
      isFalsy(router.query.userIds)
    ) {
      return;
    }

    flightSearchHandler();

    if (!router.query.date) {
      router.query.date = `${Date.now()}`;
      router.push(router);
    }

    setQueryModel(router.query);
    dispatch(setBookingFormValue("promoCode", null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, travelers?.length]);

  if (
    errorMsg &&
    ([
      INCORRECT_ERROR_CODE,
      CHILD_WITHOUT_ACCOMPANIENT_ERROR_CODE,
      DEPARTURE_BEFORE_PREVIOUS_FLIGHT_MULTICITY,
    ].includes(errorCode) ||
      errorCode === INVALID_DATE_ERROR_CODE)
  ) {
    return (
      <LegacyModalAlert
        show={errorMsg}
        onClose={hideCustomError}
        message={errorMsg}
        hideHeader
      />
    );
  }

  const handleShowMobileFilters = () => {
    const newClick = Date.now();

    if (newClick <= clickTimestamp + CLICK_DELAY) {
      return;
    }

    const nextShowMobileFilters = !showMobileFilters;

    setFilterButtonClickable(false);
    setShowMobileFilters(nextShowMobileFilters);
    document.body.style.overflowY = nextShowMobileFilters ? "hidden" : "auto";

    const btnContainer = document.querySelector<HTMLElement>(
      ".result-filter-btn-container"
    );

    if (!btnContainer) {
      setFilterButtonClickable(true);
      setClickTimestamp(Date.now());
      return;
    }

    if (nextShowMobileFilters) {
      setTimeout(() => {
        btnContainer.style.left = "230px";
      }, FILTER_OPENING_DELAY);
      setTimeout(() => {
        if (btnContainer.style.left === "230px") {
          btnContainer.classList.add("hidden");
          setFilterButtonClickable(true);
        }
      }, FILTER_OPENING_TIME);
    } else {
      btnContainer.classList.remove("hidden");
      btnContainer.style.transitionDelay = "0s";
      setTimeout(() => {
        btnContainer.style.left = "12px";
      }, ANIM_FOR_STEPPING_BACK);
      setFilterButtonClickable(true);
    }
    setClickTimestamp(Date.now());
  };

  return (
    <div
      style={{
        position: isFloating || isSearching ? "static" : "relative",
        height: isFloating ? "auto" : "100%",
      }}
      className="results-wrapper"
      id="results-wrapper"
    >
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage(title),
        }}
      />
      {!isSearching &&
        flightOffers &&
        flightOffers.length > 0 &&
        !isSomeModalOpenned && (
          <div
            style={{
              position: isFloating ? "fixed" : "relative",
              top: isFloating ? "auto" : PINNED_FROM_TOP,
              bottom: isFloating ? 30 : "auto",
              height: isFloating ? "auto" : 0,
            }}
            className="result-filter-btn-container"
          >
            <div
              role="button"
              className="result-filter-icon-container"
              onClick={() => filterButtonClickable && handleShowMobileFilters()}
            >
              <Img
                className="result-filter-icon-item"
                src="/static/images/ico-parameters.svg"
                alt="parameters icon"
              />
            </div>
          </div>
        )}
      <div className="container">
        <div className="results-wrapper-box">
          <div className="results-wrapper-box-filters">
            <Filters
              query={router.query}
              toggleMobileFilters={() => handleShowMobileFilters()}
              showMobileFilters={showMobileFilters}
              intl={intl}
              flightOffers={flightOffers}
            />
          </div>
          <div className="results-wrapper-box-rest">
            <div className="results-wrapper-box-share-box">
              <SearchSteps query={router.query} page="results" />
              <ShareModal showIcon className="pointer" />
            </div>
            <div id="results-main-div">
              {flightOffers && flightOffers.length > 0 && (
                <SearchSorting details={searchSortingDetails} />
              )}
              <div
                style={{
                  position: "relative",
                  bottom: 80,
                }}
                ref={sortingRef}
              />
              <ListFlights
                setIsSomeModalOpenned={setIsSomeModalOpenned}
                flightOffers={flightOffers}
                url={router}
                searchQuery={searchQuery}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Results.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error("Error Initial. ", e);
    return {};
  }
};

export default Results;
